/* The country whose currency and local terms the UI shows: the signed-in company's country (Saudi Arabia by default).
 * <CountryProvider> (app/App.tsx) sets it from the company settings or the signed-in user; marketing pages wrap their
 * content in <CountryScope> for the page's country. Money / moneyText use it for the currency code, symbol and decimals. */
import { createContext, useContext, type ReactNode } from "react";
import { countryOf, type CountryInfo } from "../domain/country";

const CountryContext = createContext<CountryInfo>(countryOf("SA"));
let active: CountryInfo = countryOf("SA");

/** Country of the current company (or marketing page). */
export const useCountry = (): CountryInfo => useContext(CountryContext);
/** Same, outside React (plain-text money in toasts, CSV exports, print sheets). */
export const activeCountry = (): CountryInfo => active;

/** `global` (the app-wide provider only) also sets the country used by plain-text helpers outside React. */
export function CountryScope({ country, global = false, children }: { country: CountryInfo; global?: boolean; children: ReactNode }) {
  if (global) active = country;
  return <CountryContext.Provider value={country}>{children}</CountryContext.Provider>;
}

/** Currency label: English uses the ISO code ("SAR", "AED"), Arabic the local symbol ("ر.س", "د.إ"). */
export function curLabel(c: CountryInfo, ar: boolean): string {
  return ar ? c.currency.symbol[1] : c.currency.code;
}

/** Billing is always in Saudi riyals (StartHR invoices from Saudi Arabia), whatever the company's country. */
export const BILLING_COUNTRY: CountryInfo = countryOf("SA");

/** The company's currency label for the current language: L("SAR", "ر.س") → L("AED", "د.إ") for a UAE company. */
export const curL = (L: (en: string, ar: string) => string): string => L(active.currency.code, active.currency.symbol[1]);
/** The company's currency code ("SAR", "AED", …) for plain text and print sheets. */
export const curCode = (): string => active.currency.code;
/** Overtime premium of the company's country ("150%" in Saudi Arabia, "125%" elsewhere in the GCC). */
export const otPct = (): string => Math.round(active.overtime * 100) + "%";
/** The company's country code, for domain calculations outside React hooks (payslip, eosb, …). */
export const activeCode = (): CountryInfo["code"] => active.code;
/** ID document of an employee in the company's country: [English, Arabic] ("National ID" / "Iqama" in Saudi Arabia,
 * "Emirates ID" in the UAE, "Civil ID" / "Resident card" in Oman…). */
export function idLabel(nationality: string, c: CountryInfo = active): [string, string] {
  if (c.code === "SA") return nationality === "SA" ? ["National ID", "الهوية الوطنية"] : ["Iqama", "الإقامة"];
  return nationality === c.code ? c.nationalId.citizen : c.nationalId.resident;
}
/** The currency in Arabic words ("ريال", "درهم", "دينار كويتي") for letters. */
export const curWordAr = (): string => active.currency.word[1];
